/**
 * componentDetail — shared data helpers + the DETERMINISTIC insight-line
 * generator for the chat component-detail cards (HANDOFF_CHAT_COMPONENT_DETAIL_
 * CARDS_V2). The insight line is the "screenshot moment": it must never be
 * LLM-authored and never evaluative/advisory — only positional, factual fact.
 *
 * There is no app-side covenant-context.ts (that lives in landing/); this util
 * derives everything from the extracted fields + the cohort percentile, in the
 * spirit of the existing deterministic getSummary() in compConfig.ts.
 *
 * Extracted fields arrive Evidence-V2-wrapped ({value, evidence, location,
 * confidence}) or as scalars — always read through unwrapField().
 */
import type { ComponentDetails } from '../../../api/types';
import { covenantDisplayName } from '../../../lib/covenantLabels';
import type { CKey } from '../../../components/analysis/CompCard/compConfig';

export type Fields = Record<string, unknown> | null | undefined;

/** Unwrap an Evidence-V2 field ({value,...}) or pass a scalar through. */
export function unwrapField<T = unknown>(field: unknown): T | null {
  if (field === null || field === undefined) {return null;}
  if (typeof field === 'object' && field !== null && 'value' in field) {
    return ((field as { value: T }).value ?? null) as T | null;
  }
  return field as T;
}

/** Read + unwrap a field by key. */
export function f<T = unknown>(fields: Fields, key: string): T | null {
  if (!fields) {return null;}
  return unwrapField<T>(fields[key]);
}

export type MarketPosition = 'above median' | 'below median' | 'in line with market';

/**
 * Positional language from a cohort percentile. Borrower-facing — NEVER the
 * "P42" notation (council rule: reserve P-notation for the accountant
 * dashboard). Positional, not evaluative.
 */
export function positionFromPercentile(p: number | null | undefined): MarketPosition | null {
  if (p === null || p === undefined || !Number.isFinite(p)) {return null;}
  if (p > 60) {return 'above median';}
  if (p < 40) {return 'below median';}
  return 'in line with market';
}

/** Belgian-style percent string for plain-text insight lines (comma decimal). */
export function fmtPct(v: unknown): string | null {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) {return null;}
  return `${n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')}%`;
}

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** "EURIBOR_3M" → "EURIBOR 3M". */
export function cleanBaseRate(raw: unknown): string {
  const s = raw === null ? '' : String(raw);
  return s.replace(/_/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Fees
// ---------------------------------------------------------------------------
export interface FeeItem {
  /** Human label. */
  label: string;
  /** Summable upfront EUR amount, when resolvable; null for rate-only fees. */
  eur: number | null;
  /** Rate / secondary detail shown alongside (e.g. "25 bps", "0,50%"). */
  note: string | null;
}

/**
 * Single source of truth for the fees card: the count, the EUR total, the rows
 * AND the minimum-data gate all derive from this list so they can never
 * disagree. A commitment fee in bps is a periodic RATE, not an upfront amount —
 * it is listed with a `note` but contributes null to the EUR total. We never
 * fabricate bps→EUR without a defined accrual basis.
 */
export function detectedFees(fields: Fields): FeeItem[] {
  const facility = numberOf(f(fields, 'facility_amount'));
  const items: FeeItem[] = [];

  const arrAmt = numberOf(f(fields, 'arrangement_fee_amount'));
  const arrPct = numberOf(f(fields, 'arrangement_fee_pct'));
  if (arrAmt !== null) {
    items.push({ label: 'Arrangement fee', eur: Math.round(arrAmt), note: arrPct !== null ? fmtPct(arrPct) : null });
  } else if (arrPct !== null) {
    items.push({
      label: 'Arrangement fee',
      eur: facility !== null ? Math.round((arrPct / 100) * facility) : null,
      note: fmtPct(arrPct),
    });
  }

  const commEur = numberOf(f(fields, 'commitment_fee_eur'));
  const commBps = numberOf(f(fields, 'commitment_fee_bps'));
  if (commEur !== null) {
    items.push({ label: 'Commitment fee', eur: Math.round(commEur), note: commBps !== null ? `${commBps} bps` : null });
  } else if (commBps !== null) {
    items.push({ label: 'Commitment fee', eur: null, note: `${commBps} bps` });
  }

  const agency = numberOf(f(fields, 'agency_fee'));
  if (agency !== null) {
    items.push({ label: 'Agency fee', eur: Math.round(agency), note: null });
  }

  // Legacy single total only when nothing itemized resolved.
  if (items.length === 0) {
    const totalUpfront = numberOf(f(fields, 'total_upfront_fees'));
    if (totalUpfront !== null) {
      items.push({ label: 'Upfront fees', eur: Math.round(totalUpfront), note: null });
    }
  }
  return items;
}

export function countDetectedFees(fields: Fields): number {
  return detectedFees(fields).length;
}

export function hasAnyFee(fields: Fields): boolean {
  return detectedFees(fields).length > 0;
}

/** Sum of the resolvable upfront EUR fee amounts; null when none are summable. */
export function totalFeesEur(fields: Fields): number | null {
  const sums = detectedFees(fields)
    .map((i) => i.eur)
    .filter((e): e is number => e !== null);
  return sums.length > 0 ? sums.reduce((a, b) => a + b, 0) : null;
}

// ---------------------------------------------------------------------------
// Covenants
// ---------------------------------------------------------------------------
export interface CovenantRow {
  label: string;
  threshold: string | null;
  kind: 'financial' | 'operational';
}

function asArray(v: unknown): Array<Record<string, unknown>> {
  return Array.isArray(v) ? (v as Array<Record<string, unknown>>) : [];
}

const GTE_TYPES = /coverage|dscr|ratio_min|min|solvency|current_ratio|working_capital|ebitda|net_worth/i;
const LTE_TYPES = /leverage|debt|net_debt/i;

function thresholdText(covenantType: string, value: number | null): string | null {
  if (value === null) {return null;}
  const v = `${value}`.replace('.', ',');
  if (GTE_TYPES.test(covenantType) && !LTE_TYPES.test(covenantType)) {return `≥ ${v}x`;}
  if (LTE_TYPES.test(covenantType)) {return `≤ ${v}x`;}
  return `${v}x`;
}

export function financialCovenantRows(fields: Fields): CovenantRow[] {
  const arr = asArray(f(fields, 'financial_covenants'));
  if (arr.length > 0) {
    return arr.map((c) => {
      const type = String(c.covenant_type ?? '');
      return {
        label: covenantDisplayName(type) || 'Financial covenant',
        threshold: thresholdText(type, numberOf(unwrapField(c.normalized_value))),
        kind: 'financial' as const,
      };
    });
  }
  // Legacy scalar fallback.
  const rows: CovenantRow[] = [];
  const lev = numberOf(f(fields, 'max_leverage_ratio'));
  if (lev !== null) {rows.push({ label: 'Leverage ratio', threshold: `≤ ${`${lev}`.replace('.', ',')}x`, kind: 'financial' });}
  const icr = numberOf(f(fields, 'min_interest_coverage_ratio'));
  if (icr !== null) {rows.push({ label: 'Interest coverage (ICR)', threshold: `≥ ${`${icr}`.replace('.', ',')}x`, kind: 'financial' });}
  const dscr = numberOf(f(fields, 'min_dscr'));
  if (dscr !== null) {rows.push({ label: 'Debt service coverage (DSCR)', threshold: `≥ ${`${dscr}`.replace('.', ',')}x`, kind: 'financial' });}
  return rows;
}

export function operationalCovenantRows(fields: Fields): CovenantRow[] {
  const arr = asArray(f(fields, 'operational_covenants'));
  const rows: CovenantRow[] = arr.map((c) => ({
    label: covenantDisplayName(String(c.covenant_type ?? '')) || 'Operational covenant',
    threshold: null,
    kind: 'operational' as const,
  }));
  // Legacy change-of-control flag (Text column, not in the array).
  const coc = f<string>(fields, 'change_of_control_clause');
  if (coc && !rows.some((r) => /change of control/i.test(r.label))) {
    rows.push({ label: 'Change of control', threshold: null, kind: 'operational' });
  }
  return rows;
}

/** Most common testing frequency across financial covenants, short form. */
export function testingFrequency(fields: Fields): string | null {
  const arr = asArray(f(fields, 'financial_covenants'));
  const freqs = arr.map((c) => String(c.testing_frequency ?? '').toLowerCase()).filter(Boolean);
  if (freqs.length === 0) {return null;}
  const first = freqs[0];
  if (first.startsWith('quarter')) {return 'quarterly';}
  if (first.startsWith('semi')) {return 'semi-annual';}
  if (first.startsWith('annual') || first.startsWith('year')) {return 'annual';}
  if (first.startsWith('month')) {return 'monthly';}
  return first;
}

// ---------------------------------------------------------------------------
// Security
// ---------------------------------------------------------------------------
export const SECURITY_TYPE_LABELS: Record<string, string> = {
  share_pledge: 'Share pledge',
  shares_pledge: 'Share pledge',
  business_assets_pledge: 'Pledge on business assets',
  business_pledge: 'Business pledge',
  receivables_pledge: 'Pledge on receivables',
  inventory_pledge: 'Pledge on inventory',
  real_estate_mortgage: 'Real estate mortgage',
  mortgage: 'Mortgage',
  bank_account_pledge: 'Bank account pledge',
  personal_guarantee: 'Personal guarantee',
  corporate_guarantee: 'Corporate guarantee',
  government_guarantee: 'Government guarantee',
  key_man_insurance: 'Key-man insurance',
  key_person_insurance: 'Key-person insurance',
  credit_insurance: 'Credit insurance',
  property_insurance: 'Property insurance',
  cash_collateral: 'Cash collateral',
};

export function securityTypeLabel(raw: string): string {
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (SECURITY_TYPE_LABELS[key]) {return SECURITY_TYPE_LABELS[key];}
  return cap(raw.trim().replace(/_/g, ' '));
}

export interface SecurityItem {
  label: string;
  isInsurance: boolean;
}

export function securityItems(fields: Fields): SecurityItem[] {
  const raw = f(fields, 'security_types');
  const list: SecurityItem[] = [];
  if (Array.isArray(raw)) {
    for (const t of raw) {
      const s = String(t);
      list.push({ label: securityTypeLabel(s), isInsurance: /insurance/i.test(s) });
    }
  }
  // Derived items not in the array.
  if (f(fields, 'mortgage_amount') !== null && !list.some((i) => /mortgage/i.test(i.label))) {
    list.push({ label: 'Real estate mortgage', isInsurance: false });
  }
  if (f(fields, 'shares_pledged_percentage') !== null && !list.some((i) => /share pledge/i.test(i.label))) {
    list.push({ label: 'Share pledge', isInsurance: false });
  }
  if (f<boolean>(fields, 'key_man_insurance_required') && !list.some((i) => i.isInsurance)) {
    list.push({ label: 'Key-man insurance', isInsurance: true });
  }
  return list;
}

export function hasPersonalGuarantee(fields: Fields): boolean {
  const raw = f(fields, 'security_types');
  if (Array.isArray(raw)) {
    return raw.some((t) => /personal_guarantee|personal guarantee/i.test(String(t)));
  }
  return false;
}

export function isUnsecured(fields: Fields): boolean {
  return securityItems(fields).length === 0
    && f(fields, 'guarantee_amount') === null
    && f(fields, 'collateral_description') === null;
}

function numberOf(v: unknown): number | null {
  if (v === null) {return null;}
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
export { numberOf };

// ---------------------------------------------------------------------------
// Minimum-data thresholds + degradation copy (graceful degradation matrix)
// ---------------------------------------------------------------------------
export function hasMinimumData(compKey: CKey, fields: Fields): boolean {
  switch (compKey) {
    case 'pricing':
      return f(fields, 'all_in_rate_pct') !== null || f(fields, 'margin_bps') !== null || f(fields, 'base_rate') !== null;
    case 'fee_load':
      return hasAnyFee(fields);
    case 'structure':
      return f(fields, 'tenor_months') !== null || f(fields, 'maturity_months') !== null;
    case 'conditions':
      return financialCovenantRows(fields).length > 0 || operationalCovenantRows(fields).length > 0;
    case 'security':
      // Security always renders (absence is itself the "unsecured" finding),
      // unless we have no fields at all.
      return fields !== null && fields !== undefined;
    default:
      return false;
  }
}

export const DEGRADATION_TEXT: Record<CKey, string> = {
  pricing: 'Interest rate details could not be determined from the term sheet. This may indicate a fixed-rate facility where the rate is documented separately.',
  fee_load: 'No fees were detected in the term sheet. This is unusual. Fees may be documented in a separate fee letter not included in the uploaded document.',
  structure: 'Loan maturity could not be determined from the term sheet.',
  conditions: 'No covenants were detected. This may indicate the term sheet is incomplete or that conditions are documented in a separate facility agreement.',
  security: 'Security requirements could not be determined from the term sheet.',
};

// ---------------------------------------------------------------------------
// Deterministic insight line (max ~15 words, positional, non-evaluative)
// ---------------------------------------------------------------------------
export function getComponentInsight(compKey: CKey, fields: Fields, details: ComponentDetails | null): string {
  const pos = positionFromPercentile(details?.percentile);
  switch (compKey) {
    case 'pricing': {
      const rateType = String(f(fields, 'rate_type') ?? '').toLowerCase();
      if (rateType === 'fixed') {
        if (pos) {return `Fixed rate ${pos} for comparable facilities.`;}
        const allIn = fmtPct(f(fields, 'all_in_rate_pct'));
        return allIn ? `Fixed rate of ${allIn} for the full term.` : 'Fixed-rate facility.';
      }
      if (pos) {return `Margin ${pos} for comparable facilities.`;}
      const m = numberOf(f(fields, 'margin_bps'));
      const base = cleanBaseRate(f(fields, 'base_rate'));
      return m !== null ? `${m} bps margin${base ? ` over ${base}` : ''}.` : 'Variable-rate facility.';
    }
    case 'fee_load': {
      const fees = countDetectedFees(fields);
      if (fees === 0) {return 'No fees detected. Verify fee letter availability.';}
      const noun = fees === 1 ? 'fee' : 'fees';
      return pos ? `${fees} ${noun} detected, ${pos} for comparable facilities.` : `${fees} ${noun} detected on this facility.`;
    }
    case 'structure': {
      const rt = String(f(fields, 'repayment_type') ?? '').toLowerCase();
      const grace = numberOf(f(fields, 'grace_period_months'));
      if (rt === 'bullet') {return 'Bullet repayment. Full principal due at maturity.';}
      if (grace && grace > 0) {return `${cap(rt) || 'Amortizing'} repayment with ${grace}-month grace period.`;}
      return rt ? `${cap(rt)} amortization over the term.` : 'Standard repayment structure.';
    }
    case 'conditions': {
      const fin = financialCovenantRows(fields).length;
      const op = operationalCovenantRows(fields).length;
      if (fin + op === 0) {return 'No covenants detected in the term sheet.';}
      if (fin <= 1) {return `Covenant-lite structure. ${fin} financial covenant detected.`;}
      const tf = testingFrequency(fields);
      return tf ? `${fin} financial covenants with ${tf} testing.` : `${fin} financial covenants detected.`;
    }
    case 'security': {
      if (isUnsecured(fields)) {return 'Unsecured facility. No collateral requirements detected.';}
      return hasPersonalGuarantee(fields)
        ? 'Secured facility with personal guarantee.'
        : 'Secured facility. No personal guarantee detected.';
    }
    default:
      return '';
  }
}
